import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { RootState } from "app/lib/store/store";
import {
  nextStep,
  prevStep,
  addStepsData,
} from "app/lib/store/slices/steps/personalDetails";
import { usePersonalDetails } from "app/hooks/applicant/usePersonalDetails";
import { APPLICANT } from "app/constants/apiEndPoints";

import MainTitle from "app/components/common/typography/MainTitle";
import ContinueButton from "app/components/common/buttons/Continue";
import PreviousButtom from "app/components/common/buttons/Previous";
import ProgressBar from "app/components/common/progressbar/ProgressBar";
import DynamicFormThree from "./dynamicForms/DynamicFormThree";
import { handleApiError } from "app/utils/handleApiError";

import { PersonalFormThreeData } from "app/utils/types";

const PartThree: React.FC = () => {
  const applicantId = useSelector(
    (state: RootState) => state.loanTypesSteps.savedIds.applicantID
  );
  const { mutate: submitPersonalDetails, isPending } = usePersonalDetails();
  const { applicants } = useSelector(
    (state: RootState) => state.loanTypesSteps.stepsData
  );
  const { formThree, ...others } = useSelector(
    (state: RootState) => state.personalDetails.stepsData
  );
  const dispatch = useDispatch();
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<PersonalFormThreeData>({
    defaultValues: {
      phoneNumber: "",
      landline: "",
      workNumber: "",
      email: "",
      preferredMethodOfContact: "",
    },
  });

  const onSubmit = (data: PersonalFormThreeData) => {
    const formatPhone = (value: string | undefined) =>
      value?.trim() ? (value.startsWith("+") ? value : `+${value}`) : "";

    const formattedData: PersonalFormThreeData = {
      ...data,
      phoneNumber: formatPhone(data.phoneNumber),
      email: data.email?.toLowerCase(),
    };
    dispatch(
      addStepsData({
        ...others,
        formThree: formattedData,
      })
    );

    submitPersonalDetails(
      {
        url: `${APPLICANT}/${applicantId}/third`,
        data: formattedData,
      },
      {
        onSuccess: (data: any) => {
          dispatch(nextStep());
        },
        onError: (error: any) => {
          handleApiError(error);
        },
      }
    );
  };

  useEffect(() => {
    if (formThree) {
      const cleanedData = {
        ...formThree,
        phoneNumber: formThree.phoneNumber?.replace(/^\+/, "") || "",
        landline: formThree.landline?.replace(/^\+/, "") || "",
        workNumber: formThree.workNumber?.replace(/^\+/, "") || "",
      };
      reset(cleanedData);
    } else {
      const { email, phoneNumber } = applicants || {};
      setValue("email", email || "");
      setValue("phoneNumber", (phoneNumber || "").replace(/^\+/, ""));
    }
  }, [formThree, reset]);

  return (
    <>
      <ProgressBar progress={50} MB={7} />
      <div className="w-full  sm:w-[70%] xl:w-[40%] mx-auto text-center px-4 sm:px-0">
        <MainTitle title="Personal Details" />

        <form onSubmit={handleSubmit(onSubmit)}>
          <DynamicFormThree
            control={control}
            errors={errors}
            phoneNumber="phoneNumber"
            landline="landline"
            workNumber="workNumber"
            email="email"
            preferredMethodOfContact="preferredMethodOfContact"
            disabledEmailPhone={true}
          />
          <div className="flex justify-between my-10">
            <PreviousButtom onClick={() => dispatch(prevStep())}>
              Previous
            </PreviousButtom>
            <ContinueButton type="submit" loading={isPending}>
              Next
            </ContinueButton>
          </div>
        </form>
      </div>
    </>
  );
};

export default PartThree;
